/**
 * Content Index Service
 * 마크다운 문서 인덱싱 및 검색을 관리하는 메인 서비스
 */

import type { 
  ContentDocument, 
  ContentIndex, 
  SearchOptions, 
  SearchResult,
  IndexingOptions,
  IndexingProgress,
  CacheValidationResult,
  DocumentMetadata
} from '../../types/contentIndex'
import { tokenize } from './tokenizer'
import { computeContentHash } from './utils'
import { parseMarkdown } from './markdownParser'
import { DocumentLRUCache, type CachedDocument } from './LRUCache'

/**
 * 콘텐츠 인덱스 서비스 인터페이스
 */
export interface IContentIndexService {
  /** 초기화 */
  initialize(vaultPath: string): Promise<void>
  /** 문서 인덱싱 */
  indexDocument(path: string, content?: string): Promise<ContentDocument>
  /** 문서 업데이트 */
  updateDocument(path: string, content?: string): Promise<ContentDocument>
  /** 문서 제거 */
  removeDocument(path: string): Promise<void>
  /** 검색 실행 */
  search(options: SearchOptions): Promise<SearchResult[]>
  /** 전체 인덱싱 */
  fullIndexing(options?: IndexingOptions): Promise<void>
  /** 증분 업데이트 */
  incrementalUpdate(changedPaths: string[]): Promise<void>
  /** 캐시 검증 */
  validateCache(): Promise<CacheValidationResult>
  /** 문서 가져오기 */
  getDocument(path: string): ContentDocument | undefined
  /** 통계 조회 */
  getStats(): Promise<any>
  /** 캐시에서 로드 */
  loadFromCache(): Promise<boolean>
  /** 캐시에 저장 */
  persistToCache(): Promise<void>
  /** 캐시 초기화 */
  clearCache(): Promise<void>
  /** 리소스 정리 */
  cleanup(): Promise<void>
}

/**
 * 콘텐츠 인덱스 서비스 추상 클래스
 */
export abstract class ContentIndexService implements IContentIndexService {
  protected index: ContentIndex = {
    documents: new Map(),
    invertedIndex: new Map(),
    linkGraph: new Map(),
    backlinkGraph: new Map(),
    tagIndex: new Map(),
    stats: {
      totalDocuments: 0,
      uniqueTokens: 0,
      uniqueTags: 0,
      indexSize: 0,
      lastFullIndexing: 0,
      lastUpdate: Date.now(),
      version: '1.0.0'
    }
  }
  
  protected vaultPath: string = ''
  protected isInitialized = false
  protected indexingInProgress = false
  protected forceReindex = false
  
  // LRU 메모리 캐시 추가
  protected documentCache: DocumentLRUCache = new DocumentLRUCache({
    maxSize: 200,      // 최대 200개 문서
    maxMemory: 100,    // 최대 100MB
    ttl: 600,          // 10분 TTL
    onEvict: (key, doc) => {
      console.debug(`[LRU] Evicted document from cache: ${key}`)
    }
  })
  
  /**
   * 초기화
   */
  async initialize(vaultPath: string): Promise<void> {
    this.vaultPath = vaultPath
    
    // 캐시에서 로드 시도
    const cacheLoaded = await this.loadFromCache()
    
    if (!cacheLoaded) {
      // No cache found, will perform initial indexing
    } else {
      // 캐시 유효성 검증
      const validation = await this.validateCache()
      if (!validation.isValid) {
        // Only warn if it's not simply missing cache (which is expected after clearCache)
        if (validation.reason !== 'No cache found') {
          console.warn('Cache validation failed:', validation.reason)
        } else {
          console.debug('No cache found, will perform indexing')
        }
        if (validation.changedFiles?.length || validation.deletedFiles?.length || validation.newFiles?.length) {
          // 증분 업데이트 수행
          const filesToUpdate = [
            ...(validation.changedFiles || []),
            ...(validation.newFiles || [])
          ]
          await this.incrementalUpdate(filesToUpdate)
          
          // 삭제된 파일 처리
          for (const path of validation.deletedFiles || []) {
            await this.removeDocument(path)
          }
        }
      }
    }
    
    this.isInitialized = true
  }
  
  /**
   * 문서 인덱싱
   */
  async indexDocument(path: string, content?: string): Promise<ContentDocument> {
    // LRU 캐시에서 먼저 확인
    const cached = this.documentCache.get(path)
    if (cached) {
      // 콘텐츠 해시로 유효성 검증
      if (!content) {
        content = await this.readFile(path)
      }
      const currentHash = await computeContentHash(content)
      if (cached.contentHash === currentHash) {
        console.debug(`[LRU] Cache hit for ${path}`)
        return {
          ...cached,
          indexedAt: cached.cachedAt
        } as ContentDocument
      }
    }
    
    // 콘텐츠 읽기 (제공되지 않은 경우)
    if (!content) {
      content = await this.readFile(path)
    }
    
    // 콘텐츠 해시 계산
    const contentHash = await computeContentHash(content)
    
    // 기존 문서 확인 (강제 재인덱싱이 아닌 경우만)
    const existingDoc = this.index.documents.get(path)
    if (existingDoc && existingDoc.contentHash === contentHash && !this.forceReindex) {
      // LRU 캐시에 추가
      this.documentCache.set(path, {
        ...existingDoc,
        cachedAt: Date.now(),
        sizeInBytes: content.length * 2
      })
      return existingDoc
    }
    
    if (existingDoc) {
    } else {
    }
    
    // 마크다운 파싱
    const parsed = parseMarkdown(content)
    
    // 토큰화
    const tokens = tokenize(content)
    
    // 메타데이터 생성
    const metadata: DocumentMetadata = {
      wordCount: tokens.length,
      lastIndexed: Date.now(),
      lastModified: await this.getFileModifiedTime(path),
      createdAt: await this.getFileCreatedTime(path),
      size: content.length,
      tags: parsed.tags,
      links: parsed.links,
      headings: parsed.headings,
      codeBlocks: parsed.codeBlocks,
      images: parsed.images
    }
    
    // 문서 객체 생성
    const document: ContentDocument = {
      id: path,
      path,
      title: parsed.title || this.extractTitleFromPath(path),
      content,
      contentHash,
      tokens,
      metadata,
      indexedAt: Date.now()
    }
    
    // 인덱스에 추가
    this.addToIndex(document)
    
    // LRU 캐시에 추가
    this.documentCache.set(path, {
      ...document,
      cachedAt: Date.now(),
      sizeInBytes: content.length * 2
    })
    
    // 통계 업데이트
    this.updateStats()
    
    return document
  }
  
  /**
   * 문서를 인덱스에 추가
   */
  protected addToIndex(document: ContentDocument): void {
    const oldDoc = this.index.documents.get(document.path)
    
    // 이전 문서가 있으면 인덱스에서 제거
    if (oldDoc) {
      this.removeFromIndex(oldDoc)
    }
    
    // 문서 추가
    this.index.documents.set(document.path, document)
    
    // 역인덱스 업데이트
    for (const token of document.tokens) {
      if (!this.index.invertedIndex.has(token)) {
        this.index.invertedIndex.set(token, new Set())
      }
      this.index.invertedIndex.get(token)!.add(document.id)
    }
    
    // 태그 인덱스 업데이트
    for (const tag of document.metadata.tags) {
      if (!this.index.tagIndex.has(tag)) {
        this.index.tagIndex.set(tag, new Set())
      }
      this.index.tagIndex.get(tag)!.add(document.id)
    }
    
    // 링크 그래프 업데이트
    if (document.metadata.links.length > 0) {
      this.index.linkGraph.set(document.id, new Set(document.metadata.links))
      
      // 백링크 그래프 업데이트
      for (const link of document.metadata.links) {
        if (!this.index.backlinkGraph.has(link)) {
          this.index.backlinkGraph.set(link, new Set())
        }
        this.index.backlinkGraph.get(link)!.add(document.id)
      }
    }
  }
  
  /**
   * 문서를 인덱스에서 제거
   */
  protected removeFromIndex(document: ContentDocument): void {
    // 역인덱스에서 제거
    for (const token of document.tokens) {
      const docs = this.index.invertedIndex.get(token)
      if (docs) {
        docs.delete(document.id)
        if (docs.size === 0) {
          this.index.invertedIndex.delete(token)
        }
      }
    }
    
    // 태그 인덱스에서 제거
    for (const tag of document.metadata.tags) {
      const docs = this.index.tagIndex.get(tag)
      if (docs) {
        docs.delete(document.id)
        if (docs.size === 0) {
          this.index.tagIndex.delete(tag)
        }
      }
    }
    
    // 링크 그래프에서 제거
    this.index.linkGraph.delete(document.id)
    
    // 백링크 그래프에서 제거
    for (const link of document.metadata.links) {
      const backlinks = this.index.backlinkGraph.get(link)
      if (backlinks) {
        backlinks.delete(document.id)
        if (backlinks.size === 0) {
          this.index.backlinkGraph.delete(link)
        }
      }
    }
  }
  
  /**
   * 문서 업데이트
   */
  async updateDocument(path: string, content?: string): Promise<ContentDocument> {
    return this.indexDocument(path, content)
  }
  
  /**
   * 문서 제거
   */
  async removeDocument(path: string): Promise<void> {
    const document = this.index.documents.get(path)
    if (document) {
      this.removeFromIndex(document)
      this.index.documents.delete(path)
      // LRU 캐시에서도 제거
      this.documentCache.delete(path)
      this.updateStats()
    }
  }
  
  /**
   * 증분 업데이트
   */
  async incrementalUpdate(changedPaths: string[]): Promise<void> {
    console.log(`Performing incremental update for ${changedPaths.length} files`)
    
    for (const path of changedPaths) {
      try {
        // 파일이 존재하는지 확인
        const exists = await this.fileExists(path)
        if (exists) {
          await this.indexDocument(path)
        } else {
          await this.removeDocument(path)
        }
      } catch (error) {
        console.error(`Failed to update ${path}:`, error)
      }
    }
    
    // 캐시에 저장
    await this.persistToCache()
  }
  
  /**
   * 통계 업데이트
   */
  protected updateStats(): void {
    // 기존 stats의 값들을 안전하게 보존
    const previousLastFullIndexing = this.index.stats?.lastFullIndexing || 0
    const previousVersion = this.index.stats?.version || '1.0.0'
    
    this.index.stats = {
      totalDocuments: this.index.documents.size,
      uniqueTokens: this.index.invertedIndex.size,
      uniqueTags: this.index.tagIndex.size,
      indexSize: this.calculateIndexSize(),
      lastFullIndexing: previousLastFullIndexing,
      lastUpdate: Date.now(),
      version: previousVersion
    }
  }
  
  /**
   * 인덱스 크기 계산
   */
  protected calculateIndexSize(): number {
    // 대략적인 크기 계산 (JSON 직렬화 크기 기준)
    let size = 0
    
    // 문서 크기
    for (const doc of this.index.documents.values()) {
      size += doc.content.length
      size += doc.tokens.length * 10 // 평균 토큰 길이
    }
    
    // 인덱스 구조 크기 (대략)
    size += this.index.invertedIndex.size * 50
    size += this.index.tagIndex.size * 30
    
    return size
  }
  
  /**
   * 경로에서 제목 추출
   */
  protected extractTitleFromPath(path: string): string {
    const filename = path.split('/').pop() || path
    return filename.replace(/\.md$/i, '')
  }
  
  /**
   * 문서 가져오기
   */
  getDocument(path: string): ContentDocument | undefined {
    // Remove .md extension if present to match the key format
    const key = path.replace('.md', '')
    return this.index.documents.get(key)
  }
  
  /**
   * 통계 조회
   */
  async getStats(): Promise<any> {
    // LRU 캐시의 만료된 항목 정리
    this.documentCache.cleanExpired()
    
    return {
      ...this.index.stats,
      cacheStatus: await this.getCacheStatus(),
      lruCache: this.documentCache.getStats()
    }
  }
  
  // 추상 메서드 - 플랫폼별 구현 필요
  abstract readFile(path: string): Promise<string>
  abstract fileExists(path: string): Promise<boolean>
  abstract getFileModifiedTime(path: string): Promise<number>
  abstract getFileCreatedTime(path: string): Promise<number>
  abstract loadFromCache(): Promise<boolean>
  abstract persistToCache(): Promise<void>
  abstract clearCache(): Promise<void>
  abstract cleanup(): Promise<void>
  abstract validateCache(): Promise<CacheValidationResult>
  abstract search(options: SearchOptions): Promise<SearchResult[]>
  abstract fullIndexing(options?: IndexingOptions): Promise<void>
  abstract getCacheStatus(): Promise<any>
}

/**
 * 싱글톤 인스턴스 관리
 */
let serviceInstance: IContentIndexService | null = null
let serviceInitPromise: Promise<IContentIndexService> | null = null

export async function getContentIndexService(): Promise<IContentIndexService> {
  if (serviceInstance) {
    return serviceInstance
  }
  
  // 이미 초기화 중이면 해당 Promise 반환
  if (serviceInitPromise) {
    return serviceInitPromise
  }
  
  // 초기화 시작
  serviceInitPromise = (async () => {
    // 환경에 따라 적절한 서비스 생성
    const isElectron = typeof window !== 'undefined' && (window as any).api
    
    if (isElectron) {
      // Dynamic import for Electron
      const { ElectronContentIndexService } = await import('./ElectronContentIndexService')
      serviceInstance = new ElectronContentIndexService()
    } else {
      // Dynamic import for Browser
      const { BrowserContentIndexService } = await import('./BrowserContentIndexService')
      serviceInstance = new BrowserContentIndexService()
    }
    
    return serviceInstance
  })()
  
  return serviceInitPromise
}

export function clearContentIndexService(): void {
  serviceInstance = null
  serviceInitPromise = null
}